// frontend/src/components/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { toast } from 'react-toastify';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Hier können Sie den Fehler an einen Dienst wie Sentry loggen
    //console.error('Unbehandelte Komponenten-Fehler:', error, errorInfo);
    
    // Fehler per Toast anzeigen
    toast.error(`Ein Fehler ist aufgetreten: ${error.message}`);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined });
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: undefined
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Benutzerdefiniertes Fallback oder Standard-Komponente
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Etwas ist schiefgelaufen
            </Typography>
            <Typography color="textSecondary" paragraph>
              Die Komponente konnte nicht angezeigt werden. 
              {this.state.error?.message && (
                <span> Fehler: {this.state.error.message}</span>
              )}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleRetry}
              sx={{ mt: 2 }}
            >
              Erneut versuchen
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;